interface ZakRecipient {
  id: string;
  title: string;
  firstname: string;
  lastname: string;
  language: string;
  birthday: string;
  phone_no: string;
  phone_no2: string;
  email: string;
  street1: string;
  zipcode: string;
  town: string;
  country: string;
}

interface ZakInvoice {
  invoice_no: string;
  invoice_file: string;
  invoice_date: string;
  treatment_from: string;
  treatment_until: string;
  lab_amount: number; //TODO Implement with accounting update
  down_payment: number; //TODO Implement with accounting update
  invoice_amount: number;
  recipient: ZakRecipient;
  patient: ZakRecipient;
  pdf_data?: {
    invoice: any;
    client: any;
    guarantor: any;
    job_documents_invoices_relationships: any[];
  };
}

interface ZakExportData {
  order_no: number;
  contract_no: string;
  order_date: string;
  invoice_count: number;
  total_amount: number;
  system: string;
  comment: string;
  invoices: ZakInvoice[];
  organization?: any;
  zak_credentials: {
    username: string;
    password: string;
  };
}

export function generateZakXml(data: ZakExportData): string {
  const escapeXml = (str: string | number): string => {
    if (typeof str === 'number') return str.toString();
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  // Returns `<name>value</name>` with filters/escaping or `<name/>` if empty after filters.
  const field = (
    name: string,
    value: string | number | null | undefined,
    ...filters: Array<(s: string) => string>
  ): string => {
    if (value === null || value === undefined) return `<${name}/>`;
    let s = String(value);
    for (const f of filters) s = f(s);
    s = s.trim();
    if (!s) return `<${name}/>`;
    return `<${name}>${escapeXml(s)}</${name}>`;
  };

  const formatAmount = (amount: number): string => {
    return amount.toFixed(2);
  };

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += `<order order_no="${escapeXml(data.order_no)}">\n`;
  xml += `  ${field('contract_no', data.contract_no)}\n`;
  xml += `  ${field('order_date', data.order_date)}\n`;
  xml += `  ${field('invoice_count', data.invoice_count)}\n`;
  xml += `  ${field('total_amount', formatAmount(data.total_amount))}\n`;
  xml += `  ${field('system', data.system)}\n`;
  xml += `  ${field('comment', data.comment)}\n`;

  // Generate invoice sections
  data.invoices.forEach((invoice) => {
    xml += `  <invoice invoice_no="${escapeXml(invoice.invoice_no)}">\n`;
    xml += `    ${field('invoice_file', invoice.invoice_file)}\n`;
    xml += `    ${field('invoice_date', invoice.invoice_date)}\n`;
    xml += `    ${field('treatment_from', invoice.treatment_from)}\n`;
    xml += `    ${field('treatment_until', invoice.treatment_until)}\n`;
    xml += `    ${field('lab_amount', formatAmount(invoice.lab_amount))}\n`;
    xml += `    ${field('down_payment', formatAmount(invoice.down_payment))}\n`;
    xml += `    ${field('invoice_amount', formatAmount(invoice.invoice_amount))}\n`;

    // Generate recipient section
    const recipient = invoice.recipient;
    xml += `    <recipient>\n`;
    xml += `      ${field('title', recipient.title)}\n`;
    xml += `      ${field('firstname', recipient.firstname)}\n`;
    xml += `      ${field('lastname', recipient.lastname)}\n`;
    xml += `      ${field('language', recipient.language)}\n`;
    xml += `      ${field('birthday', recipient.birthday)}\n`;
    xml += `      ${field('phone_no', recipient.phone_no)}\n`;
    xml += `      ${field('phone_no2', recipient.phone_no2)}\n`;
    xml += `      ${field('email', recipient.email)}\n`;
    xml += `      ${field('street1', recipient.street1)}\n`;
    xml += `      ${field('zipcode', recipient.zipcode)}\n`;
    xml += `      ${field('town', recipient.town)}\n`;
    xml += `      ${field('country', recipient.country)}\n`;
    xml += `    </recipient>\n`;

    // Generate patient section if different from recipient
    if (invoice.patient) {
      const patient = invoice.patient;
      xml += `    <patient>\n`;
      xml += `      ${field('title', patient.title)}\n`;
      xml += `      ${field('firstname', patient.firstname)}\n`;
      xml += `      ${field('lastname', patient.lastname)}\n`;
      xml += `      ${field('language', patient.language)}\n`;
      xml += `      ${field('birthday', patient.birthday)}\n`;
      xml += `      ${field('phone_no', patient.phone_no)}\n`;
      xml += `      ${field('phone_no2', patient.phone_no2)}\n`;
      xml += `      ${field('email', patient.email)}\n`;
      xml += `      ${field('street1', patient.street1)}\n`;
      xml += `      ${field('zipcode', patient.zipcode)}\n`;
      xml += `      ${field('town', patient.town)}\n`;
      xml += `      ${field('country', patient.country)}\n`;
      xml += `    </patient>\n`;
    }

    xml += `  </invoice>\n`;
  });

  xml += '</order>';

  return xml;
}

export type { ZakExportData, ZakInvoice, ZakRecipient };